/**
 * Platform-agnostic haptic feedback.
 *
 * Runtime picks the best available API, in order of fidelity:
 *   1. Telegram WebApp HapticFeedback  (native-feeling inside Mini App)
 *   2. navigator.vibrate                (web + Capacitor Android; iOS
 *                                        Safari ignores per Apple policy
 *                                        but the call is harmless)
 *   3. No-op                            (SSR / unsupported environments)
 *
 * Keeping this in @turnifly/shared lets stores and hooks ship a single
 * codepath while the UI shell in each consumer remains free to call
 * platform-specific APIs directly for more specialised effects.
 */

type NotificationStyle = 'success' | 'error' | 'warning'
type ImpactStyle = 'light' | 'medium' | 'heavy'

interface TelegramHapticFeedback {
  notificationOccurred?: (style: NotificationStyle) => void
  impactOccurred?: (style: ImpactStyle) => void
}

interface TelegramWebAppShape {
  HapticFeedback?: TelegramHapticFeedback
}

function getTelegramHaptic(): TelegramHapticFeedback | null {
  if (typeof window === 'undefined') return null
  const tg = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppShape } }).Telegram?.WebApp
  return tg?.HapticFeedback ?? null
}

function webVibrate(pattern: number | number[]): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(pattern) } catch { /* Safari sometimes throws; ignore */ }
  }
}

export function triggerHapticNotification(style: NotificationStyle): void {
  const tg = getTelegramHaptic()
  if (tg?.notificationOccurred) {
    try { tg.notificationOccurred(style); return } catch { /* fall through */ }
  }
  // Web fallback — distinct patterns per style
  const pattern = style === 'error' ? [50, 30, 50] : style === 'success' ? [30] : [20]
  webVibrate(pattern)
}

export function triggerHapticImpact(style: ImpactStyle = 'light'): void {
  const tg = getTelegramHaptic()
  if (tg?.impactOccurred) {
    try { tg.impactOccurred(style); return } catch { /* fall through */ }
  }
  const ms = style === 'heavy' ? 30 : style === 'medium' ? 15 : 10
  webVibrate(ms)
}
